import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './DonationAmountRadioGroup.styled';
import { FREQUENCY } from '../../../models/DonationFrequency';
import { formatCurrency } from '../../../features/passport/formatCurrency';
import * as SkeletonLoader from './SkeletonLoader';
import { useResizeFontToFit } from '../../../features/passport/useResizeFontToFit';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The Donation Amount "Slider" that is actually a styled group of Radio Inputs.
 *
 * Clicking the "other" Donation Amount radio input automatically moves browser focus to the
 * Other Amount Input.
 */
export const DonationAmountRadioGroup = _ref => {
  let {
    amounts,
    activeDonationAmountName,
    otherAmountInputRef,
    dispatch,
    frequency,
    locale,
    currencyCode,
    loadingState
  } = _ref;
  const {
    t
  } = useTranslation('blocks.impact');
  const indexOfSelectedAmount = amounts.findIndex(preset => preset.name === activeDonationAmountName);
  const containerRef = React.useRef(null);
  const resizeFontToFixDependencies = React.useMemo(() => [frequency, currencyCode], [frequency, currencyCode]);
  useResizeFontToFit({
    containerRef,
    elementClassName: 'impact-amount-button',
    // keep in sync with Styled.RadioInputLabel
    defaultFontSize: 16,
    externalDependency: resizeFontToFixDependencies
  });
  if (loadingState === 'loading') {
    return /*#__PURE__*/_jsx(SkeletonLoader.DonationAmountRadioGroupFieldset, {});
  }
  return /*#__PURE__*/_jsxs(Styled.DonationAmountRadioGroupFieldset, {
    ref: containerRef,
    "aria-label": t('DonationAmountRadioGroup.fieldset.ariaLabel', 'Donation amount'),
    doFadeIn: loadingState === 'loaded',
    children: [/*#__PURE__*/_jsx(Styled.Slider, {
      position: indexOfSelectedAmount,
      numRadioButtons: amounts.length
    }), amounts.map((preset, index) => {
      const formattedAmount = formatCurrency({
        amount: preset.amount,
        locale,
        currencyCode,
        options: {
          trailingZeroDisplay: 'stripIfInteger'
        }
      });
      if (preset.name.startsWith('preset')) {
        return /*#__PURE__*/_jsxs(Styled.DonationAmountRadioInputWrapper, {
          className: "impact-amount-button",
          children: [/*#__PURE__*/_jsx(Styled.RadioInputLabel, {
            isSelected: activeDonationAmountName === preset.name,
            children: formattedAmount
          }), /*#__PURE__*/_jsx(Styled.DonationAmountRadioInput, {
            type: "radio"
            /**
             * Ideally, we should be using htmlFor to associate the label with the input.
             * But, if there are multiple Impact blocks, the screen reader will repeat the
             * label text for every Impact block instance with the same preset. Aria-label is
             * a workaround (in lieu of passing down the block's ID to insert into the htmlFor
             * attribute).
             */,
            "aria-label": formattedAmount,
            name: "donation-amount",
            id: String(preset.amount),
            "data-id": `cp-impact-${frequency === FREQUENCY.ONE_TIME ? 'onetime' : 'recurring'}-preset-${index + 1}`,
            checked: activeDonationAmountName === preset.name,
            onChange: () => {
              dispatch({
                type: 'SELECT_PRESET_AMOUNT_BUTTON',
                donationAmount: preset
              });
            },
            isSelected: activeDonationAmountName === preset.name
          })]
        }, preset.name);
      }
      const otherAmountFormatted = preset.name === 'other' && otherAmountInputRef.current?.value ? formatCurrency({
        amount: otherAmountInputRef.current?.value,
        currencyCode,
        locale
      }) : '';
      return preset.name === 'other' && /*#__PURE__*/_jsxs(Styled.DonationAmountRadioInputWrapper, {
        children: [/*#__PURE__*/_jsx(Styled.RadioInputLabel, {
          isSelected: activeDonationAmountName === preset.name,
          children: t('DonationAmountRadioGroup.other.label', 'Other')
        }), /*#__PURE__*/_jsx(Styled.DonationAmountRadioInput, {
          type: "radio"
          // eg 'Other' or 'Other: $100'
          ,
          "aria-label": t('DonationAmountRadioGroup.other.ariaLabel', {
            amountValue: otherAmountFormatted,
            defaultValue: 'Other {{amountValue}}'
          }),
          name: "donation-amount",
          id: "other",
          "data-id": `cp-impact-${frequency === FREQUENCY.ONE_TIME ? 'onetime' : 'recurring'}-custom`,
          checked: activeDonationAmountName === preset.name,
          onChange: () => {
            dispatch({
              type: 'SELECT_OTHER_AMOUNT_BUTTON'
            });

            // Move focus to the Other Amount Input
            if (otherAmountInputRef.current) {
              otherAmountInputRef.current.focus();
            }
          },
          isSelected: activeDonationAmountName === preset.name
        })]
      }, preset.name);
    })]
  });
};